<template>
  <b-row class="no-gutters">
    <b-col cols="11">
      <vue-slider
        v-model="values"
        class="mt-4"
        :interval="1"
        :enable-cross="true"
        :min="0"
        :max="maxTimeValue"
        :marks="marks"
        :clickable="false"
        :tooltip-formatter="timeFormatter"
        :process="process"
        @drag-end="dragEnd"
        @change="sliderChanged"
        :tooltipStyle="{ width: 40 +'px', height: 40 +'px' }">
        <template v-slot:tooltip="{ value, focus }">
          <div
            class="vue-slider-dot-tooltip vue-slider-dot-tooltip-top"
            :class="[{ focus, 'vue-slider-dot-tooltip-show': focus }]">
            <div class="vue-slider-dot-tooltip-inner vue-slider-dot-tooltip-inner-top">
            <span
              class="vue-slider-dot-tooltip-text">
              {{ timeFormatter(value) }}
            </span>
            </div>
          </div>
        </template>
        <template v-slot:dot="{ value, focus }">
          <div
            :class="['custom-dot', { focus }]"
            @click="dotClick(value)"
            @touchend="dotClick(value)"
            @mousemove="dragging(value)"
            @touchmove="dragging(value)"
            @mousedown="mousedown"
          >
            <component
              :is="translateVarsValueIntoComponent(getSettingsValue(value))"
              :value="getSettingsValue(value)"
            />
          </div>
        </template>
        <template v-slot:mark="{ pos, label, value }">
          <div
            :class="['custom-mark', { highlight: activeCol === Math.floor(value / 4) }]"
            :data-pos="value"
            :style="{ left: `${pos}%` }">
            {{ label }}
          </div>
        </template>
      </vue-slider>
    </b-col>
    <b-col cols="1">
      <div
        class="pl-4"
        :style="{ position: 'absolute', top: '20px' }"
      >
        <round-icon-button
          v-if="enabledCount < maxPointCount"
          icon="plus"
          transform="shrink-8 right-0.9"
          @click="addValuePoint"
        />
        <round-icon-button
          v-if="enabledCount > 0"
          class="ml-4"
          icon="trash-alt"
          transform="shrink-8 right-0.9"
          @click="removeValuePoint"
        />
      </div>
    </b-col>
  </b-row>
</template>

<script>
import VueSlider from 'vue-slider-component';
import { mapActions } from 'vuex';
import NumberVal from '@/components/services/service/scheduler/value-display/NumberVal';
import BooleanVal from '@/components/services/service/scheduler/value-display/BooleanVal';
import RoundIconButton from '@/components/utils/RoundIconButton';

export default {
  name: 'Timeline',
  data() {
    return {
      isDragging: false,
      maxPointCount: 4,
      maxTimeValue: 95,
      valuesCache: [],
      marks: val => val % 4 === 0 && val / 4 !== 24 ? ({
        label: this.showMarks ? `${val / 4}`.slice(-2) : '',
      }) : false,
      process: false,
      editTriggered: false,
    };
  },
  props: {
    points: {
      type: Array,
    },
    showMarks: {
      type: Boolean,
      default: true,
    },
    activeCol: {
      type: Number,
      default: null,
    },
    autoEnable: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  computed: {
    settings() {
      return this.points.map(point => point.value.value);
    },
    enableVars() {
      return this.points.map(point => point.enable);
    },
    enabledVars() {
      return this.enableVars.filter(varItem => varItem.value);
    },
    enabledCount() {
      return this.enabledVars.length;
    },
    values: {
      get() {
        return this.valuesCache;
      },
      set(value) {
        this.valuesCache = Array.isArray(value) ? value : [value];
      },
    },
  },
  methods: {
    ...mapActions([
      'setVarsValue',
    ]),
    addValuePoint() {
      if (this.enabledCount < this.maxPointCount) {
        const point = this.points[this.enabledCount];
        this.setVarsValue({
          varItem: point.enable,
          value: true,
        });
        this.setVarsValue({
          varItem: point.time,
          value: this.convertSchedulersTimeToTime(this.maxTimeValue),
        });
        if (point.value.type === 'BOOL' && point.value.value === false && this.autoEnable) {
          this.setVarsValue({
            varItem: point.value,
            value: true,
          });
        }
        this.updateValues();
      }
    },
    removeValuePoint() {
      if (this.enabledCount > 0) {
        this.setVarsValue({
          varItem: this.enabledVars[this.enabledCount - 1],
          value: false,
        });
        this.updateValues();
      }
    },
    updateValues() {
      this.values = this.points.map(
        point => this.convertTimeToSchedulersTime(point.time.value),
      ).splice(0, this.enabledCount);
    },
    timeFormatter: val => `${Math.floor(val / 4)}:${`0${(val % 4) * 15}`.slice(-2)}`,
    translateVarsValueIntoComponent(value) {
      if (typeof value === 'boolean') {
        return BooleanVal;
      }
      return NumberVal;
    },
    getSettingsValue(value) {
      return this.settings[this.values.indexOf(value)];
    },
    getSettingsValueVar(value) {
      return this.points[this.values.indexOf(value)].value;
    },
    updateVars(values) {
      for (let index = 0; index < values.length; index += 1) {
        const point = this.points[index];
        const convertedValue = this.convertSchedulersTimeToTime(values[index]);
        if (point.time.value !== convertedValue) {
          this.setVarsValue({
            varItem: point.time,
            value: convertedValue,
          });
          this.setVarsValue({
            varItem: point.value,
            value: point.value.value,
          });
        }
      }
    },
    dotPointerOut() {
      this.$emit('pos-changed', null);
    },
    mousedown() {
      this.editTriggered = true;
    },
    sliderChanged() {
      this.editTriggered = false;
    },
    dragging(value) {
      this.$emit('pos-changed', value);
    },
    dragEnd() {
      this.isDragging = false;
      this.updateVars(this.valuesCache);
      this.$emit('drag-end');
    },
    dotClick(value) {
      if (this.editTriggered && !this.autoEnable) {
        this.$emit('dot-click', this.getSettingsValueVar(value));
        this.editTriggered = false;
      }
    },
    convertTimeToSchedulersTime(value) {
      // given value is in milliseconds.
      // Which we have to convert to minutes and then to hours quarters units
      return Math.round(value / 60 / 15 / 1000);
    },
    convertSchedulersTimeToTime(value) {
      return value * 15 * 60 * 1000; // time should be in ms
    },
  },
  mounted() {
    this.updateValues();
  },
  watch: {
    points() {
      this.updateValues();
    },
  },
  components: {
    RoundIconButton,
    VueSlider,
  },
};
</script>


<style lang="scss" scoped>
@import '@/assets/scss/settings.scss';

.custom-mark {
  position: absolute;
  top: -50px;
  margin-left: 2%;
  padding-left: 4px;
  transform: translateX(-50%);
  font-weight: bold;
  font-size: 1rem;
  border-left: 2px solid #d4d4d4;
  width: 4.16667%;
  height: 50px;

  &.highlight {
    background-color: $light;
  }
}

.vue-slider-dot-tooltip-top {
  display: none;
}

.focus {
  display: block;

  .vue-slider-dot-tooltip-text {
    width: 40px;
    height: 40px;
    font-weight: bold;
  }
}

.custom-dot {
  position: absolute;
  top: -8px;
  left: -8px;
  width: 30px;
  height: 30px;
  border-radius: 50%;
  color: white;
  background-color: $info;
  transition: all .3s;
  cursor: pointer;
  text-align: center;
  font-weight: bold;
  padding-top: 3px;
}

.custom-dot:hover {
}

.custom-dot.focus {
}
</style>
